import { inject, Injectable } from '@angular/core';
import { asapScheduler, firstValueFrom, observeOn, take } from 'rxjs';

import { AuthService } from '@blaze-shared/auth-data-access';
import { NavMenu } from '@blaze-shared/elements-domain';
import { NotificationService } from '@blaze-shared/platform-data-access';

import { ElementsFacade } from '../../+state/elements/elements.facade';
import { LayoutLoadingService } from '../../services/layout-loading.service';
import { ProjectNavigateService } from '../../services/project-navigate.service';

@Injectable({ providedIn: 'root' })
export class NavMenuService {
  private readonly authService = inject(AuthService);
  private readonly elementsFacade = inject(ElementsFacade);
  private readonly layoutLoadingService = inject(LayoutLoadingService);
  private readonly notificationService = inject(NotificationService);
  private readonly projectNavigate = inject(ProjectNavigateService);

  async handleItemClick(item: NavMenu.RenderItem): Promise<void> {
    const layoutEntities = await firstValueFrom(this.elementsFacade.layoutEntities$);

    switch (item.props?.clickAction) {
      case 'GoToPage': {
        const pageId = item.props?.actionParams;
        try {
          if (layoutEntities?.[pageId]) {
            this.projectNavigate.navigateToPage({ targetPageId: pageId });
          } else {
            this.layoutLoadingService
              .loadAndWait(pageId)
              .pipe(take(1), observeOn(asapScheduler))
              .subscribe(() => {
                this.projectNavigate.navigateToPage({ targetPageId: pageId });
              });
          }
        } catch {
          this.notificationService.error('The clicked menu item is missing the page param', true);
        }
        break;
      }
      case 'Logout':
        this.authService.signOut(true);
        break;
      case 'Url':
        if (!item.props?.navigateUrl) {
          this.notificationService.error(
            'The clicked menu item is missing a valid website address',
            true,
          );
        } else {
          window.open(item.props?.navigateUrl, '_blank', 'noreferrer noopener');
        }
        break;
      default:
        break;
    }
  }
}
